import React from 'react';
import { startOfDay, endOfDay, parseISO } from 'date-fns';
import { Task, Client, Driver, Dumpster, User } from '../../types';
import TaskCard from './TaskCard';

interface TaskListProps {
  tasks: Task[];
  clients: Client[];
  drivers: Driver[];
  users: User[];
  dumpsters: Dumpster[];
  onOpenProductsModal?: (task: Task) => void;
  onOpenPhotoSignatureModal?: (task: Task) => void;
  onCancelTask?: (task: Task) => void;
  onCompleteTask?: (task: Task) => void;
  onStartTask?: (task: Task) => void;
  onSetTaskToWaiting?: (task: Task) => void;
  onDeleteTask?: (task: Task) => void;
  onEditTask?: (task: Task) => void;
  canCompleteTask?: boolean;
  isAdmin?: boolean;
  isLogistics?: boolean;
}

const TaskList: React.FC<TaskListProps> = ({
  tasks,
  clients,
  drivers,
  users,
  dumpsters,
  onOpenProductsModal,
  onOpenPhotoSignatureModal,
  onCancelTask,
  onCompleteTask,
  onStartTask,
  onSetTaskToWaiting,
  onDeleteTask,
  onEditTask,
  canCompleteTask,
  isAdmin,
  isLogistics
}) => {
  // Get today's start and end
  const today = new Date();
  const todayStart = startOfDay(today);
  const todayEnd = endOfDay(today);

  // Filter tasks: show all active tasks (pending, waiting, in-progress, editing) regardless of date,
  // but only show today's completed/cancelled tasks
  const todayTasks = tasks.filter(task => {
    // Always show active tasks regardless of date
    if (task.status === 'pending' || task.status === 'waiting' || task.status === 'in-progress' || task.status === 'editing') {
      return true;
    }
    
    // For completed and cancelled tasks, only show today's tasks
    let taskDate: Date;
    
    if (task.status === 'completed' && task.endTime) {
      taskDate = parseISO(task.endTime);
    } else if (task.status === 'cancelled' && task.cancelledAt) {
      taskDate = parseISO(task.cancelledAt);
    } else {
      taskDate = parseISO(task.date);
    }
    
    return taskDate >= todayStart && taskDate <= todayEnd;
  });

  // Group today's tasks by status
  const groupedTasks = todayTasks.reduce((acc, task) => {
    if (!acc[task.status]) {
      acc[task.status] = [];
    }
    acc[task.status].push(task);
    return acc;
  }, {} as Record<string, Task[]>);

  return (
    <div className="space-y-8">
      {groupedTasks.editing && groupedTasks.editing.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold mb-4">En modification ({groupedTasks.editing.length})</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {groupedTasks.editing.map(task => (
              <TaskCard
                key={task.id}
                task={task}
                client={clients.find(c => c.id === task.clientId)}
                driver={drivers.find(d => d.id === task.driverId)}
                drivers={drivers}
                users={users}
                dumpster={task.type === 'dumpster' ? dumpsters.find(d => d.id === task.dumpsterId) : undefined}
                onOpenProductsModal={onOpenProductsModal}
                onOpenPhotoSignatureModal={onOpenPhotoSignatureModal}
                onCancelTask={onCancelTask}
                onStartTask={onStartTask}
                onCompleteTask={onCompleteTask}
                onSetTaskToWaiting={onSetTaskToWaiting}
                onDeleteTask={isAdmin ? onDeleteTask : undefined}
                onEditTask={onEditTask}
                canCompleteTask={canCompleteTask}
                isAdmin={isAdmin}
                isLogistics={isLogistics}
              />
            ))}
          </div>
        </div>
      )}

      {groupedTasks.pending && groupedTasks.pending.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold mb-4">En attente ({groupedTasks.pending.length})</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {groupedTasks.pending.map(task => (
              <TaskCard
                key={task.id}
                task={task}
                client={clients.find(c => c.id === task.clientId)}
                driver={drivers.find(d => d.id === task.driverId)}
                drivers={drivers}
                users={users}
                dumpster={task.type === 'dumpster' ? dumpsters.find(d => d.id === task.dumpsterId) : undefined}
                onOpenProductsModal={onOpenProductsModal}
                onOpenPhotoSignatureModal={onOpenPhotoSignatureModal}
                onCancelTask={onCancelTask}
                onStartTask={onStartTask}
                onCompleteTask={onCompleteTask}
                onSetTaskToWaiting={onSetTaskToWaiting}
                onDeleteTask={isAdmin ? onDeleteTask : undefined}
                onEditTask={onEditTask}
                canCompleteTask={canCompleteTask}
                isAdmin={isAdmin}
                isLogistics={isLogistics}
              />
            ))}
          </div>
        </div>
      )}

      {groupedTasks.waiting && groupedTasks.waiting.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold mb-4">En attente de conditions ({groupedTasks.waiting.length})</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {groupedTasks.waiting.map(task => (
              <TaskCard
                key={task.id}
                task={task}
                client={clients.find(c => c.id === task.clientId)}
                driver={drivers.find(d => d.id === task.driverId)}
                drivers={drivers}
                users={users}
                dumpster={task.type === 'dumpster' ? dumpsters.find(d => d.id === task.dumpsterId) : undefined}
                onOpenProductsModal={onOpenProductsModal}
                onOpenPhotoSignatureModal={onOpenPhotoSignatureModal}
                onCancelTask={onCancelTask}
                onStartTask={onStartTask}
                onCompleteTask={onCompleteTask}
                onSetTaskToWaiting={onSetTaskToWaiting}
                onDeleteTask={isAdmin ? onDeleteTask : undefined}
                onEditTask={onEditTask}
                canCompleteTask={canCompleteTask}
                isAdmin={isAdmin}
                isLogistics={isLogistics}
              />
            ))}
          </div>
        </div>
      )}

      {groupedTasks['in-progress'] && groupedTasks['in-progress'].length > 0 && (
        <div>
          <h3 className="text-lg font-semibold mb-4">En cours ({groupedTasks['in-progress'].length})</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {groupedTasks['in-progress'].map(task => (
              <TaskCard
                key={task.id}
                task={task}
                client={clients.find(c => c.id === task.clientId)}
                driver={drivers.find(d => d.id === task.driverId)}
                drivers={drivers}
                users={users}
                dumpster={task.type === 'dumpster' ? dumpsters.find(d => d.id === task.dumpsterId) : undefined}
                onOpenProductsModal={onOpenProductsModal}
                onOpenPhotoSignatureModal={onOpenPhotoSignatureModal}
                onCancelTask={onCancelTask}
                onCompleteTask={onCompleteTask}
                onSetTaskToWaiting={onSetTaskToWaiting}
                onDeleteTask={isAdmin ? onDeleteTask : undefined}
                onEditTask={onEditTask}
                canCompleteTask={canCompleteTask}
                isAdmin={isAdmin}
                isLogistics={isLogistics}
              />
            ))}
          </div>
        </div>
      )}

      {groupedTasks.completed && groupedTasks.completed.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold mb-4">Terminées ({groupedTasks.completed.length})</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {groupedTasks.completed.map(task => (
              <TaskCard
                key={task.id}
                task={task}
                client={clients.find(c => c.id === task.clientId)}
                driver={drivers.find(d => d.id === task.driverId)}
                drivers={drivers}
                users={users}
                dumpster={task.type === 'dumpster' ? dumpsters.find(d => d.id === task.dumpsterId) : undefined}
                onOpenProductsModal={onOpenProductsModal}
                onOpenPhotoSignatureModal={onOpenPhotoSignatureModal}
                onDeleteTask={isAdmin ? onDeleteTask : undefined}
                isAdmin={isAdmin}
                isLogistics={isLogistics}
              />
            ))}
          </div>
        </div>
      )}

      {groupedTasks.cancelled && groupedTasks.cancelled.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold mb-4">Annulées ({groupedTasks.cancelled.length})</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {groupedTasks.cancelled.map(task => (
              <TaskCard
                key={task.id}
                task={task}
                client={clients.find(c => c.id === task.clientId)}
                driver={drivers.find(d => d.id === task.driverId)}
                drivers={drivers}
                users={users}
                dumpster={task.type === 'dumpster' ? dumpsters.find(d => d.id === task.dumpsterId) : undefined}
                onOpenProductsModal={onOpenProductsModal}
                onOpenPhotoSignatureModal={onOpenPhotoSignatureModal}
                onDeleteTask={isAdmin ? onDeleteTask : undefined}
                isAdmin={isAdmin}
                isLogistics={isLogistics}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskList;